import _ from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { PaymentRequestStatusType, PaymentRequestType } from 'types/cashFlow'
import { ExpectedMilestonePaymentType } from 'types/paymentOptions'
import { RootState } from 'types/state'
import { getInvoiceModeDisplay, InvoiceModeDisplay, InvoiceModeValue } from './types'

const restClientInstance = restClient(window.API_ROOT + '/api')

// Simple counter to trigger refresh across all hook instances
let invoiceModeRefreshCounter = 0
const triggerInvoiceModeRefresh = () => {
  invoiceModeRefreshCounter++
}

export const useIsCalculating = (systemUuid: string | undefined | null) => {
  const systemCalculation = useSelector((state: RootState) => state.designer.systemCalculation)
  const { processQueue } = systemCalculation
  const isCalculating = systemUuid && processQueue[systemUuid]?.status === 'waitingCalculation'

  return Boolean(isCalculating)
}

export const useAnySystemIsCalculating = () => {
  const systemCalculation = useSelector((state: RootState) => state.designer.systemCalculation)
  const { processQueue } = systemCalculation
  let isCalculating = false
  if (processQueue) {
    for (const systemUuid in processQueue) {
      if (processQueue[systemUuid]?.status === 'waitingCalculation') {
        isCalculating = true
        break
      }
    }
  }

  return isCalculating
}

export const createNewMilestone = (
  existingMilestones: ExpectedMilestonePaymentType[]
): ExpectedMilestonePaymentType => {
  const newMilestone: ExpectedMilestonePaymentType = {
    percentage: 0,
    title: '',
    description: null,
    payment_amount: 0,
    currency: existingMilestones[0]?.currency,
    is_paid: false,
    is_next: false,
    payment_number: existingMilestones?.length,
    payment_milestone_configuration_id: null,
    payment_request_id: null,
    date_requested: null,
    date_due: null,
    date_completed: null,
    is_deposit: false,
  }
  return newMilestone
}

export const updatPriceOverrideAndRecalc = (systemUuid: string, paymentOptionId: number, systemPrice: string) => {
  const system = window.editor.getSystems()?.find((sys) => sys.uuid === systemUuid)
  let priceOverride = _.cloneDeep(system?.payment_options_settings_overrides) || {}
  // force the system to not use auto-applied payment options since we are now overriding one
  let overidePaymentOptionIds = system?.payment_options_override || system?.payment_options.map((po) => po.id)
  overidePaymentOptionIds = overidePaymentOptionIds?.filter((value, index, self) => {
    return self.indexOf(value) === index
  })
  if (!priceOverride?.[paymentOptionId]) {
    priceOverride[paymentOptionId] = {
      down_payment: 0,
      price: parseFloat(systemPrice),
    }
  } else {
    priceOverride[paymentOptionId].price = parseFloat(systemPrice)
  }
  window.editor.execute(new window.SetValueCommand(system, 'payment_options_override', overidePaymentOptionIds))
  window.editor.execute(new window.SetValueCommand(system, 'payment_options_settings_overrides', priceOverride))
}

export const useGetLastPaymentRequestForMilestone = (
  allPaymentRequests: PaymentRequestType[],
  milestone: ExpectedMilestonePaymentType
) => {
  const lastRequest = useMemo(() => {
    if (!allPaymentRequests?.length) return null
    const matchingRequests = allPaymentRequests?.filter((req) => req.payment_number === milestone.payment_number)
    if (!matchingRequests?.length) return null
    return matchingRequests[matchingRequests.length - 1]
  }, [milestone, allPaymentRequests])

  return lastRequest
}
export const roundNumberToTwoDecimals = (num: number | undefined | null) => {
  return num ? Math.round(num * 100) / 100 : 0
}

/** Max allowed drift between milestone sum and amount payable from penny rounding (GS-5734). */
export const MILESTONE_AMOUNT_ROUNDING_TOLERANCE = 0.01

export const milestoneAmountsDeviateFromTotal = (
  milestoneSum: number,
  systemPriceIncludingTax: number | undefined,
  amountPayable: number | undefined
): boolean => {
  const roundedSum = parseFloat(Number(milestoneSum).toFixed(2))
  const systemPrice = roundNumberToTwoDecimals(systemPriceIncludingTax)
  const payable = roundNumberToTwoDecimals(amountPayable)
  if (roundedSum > systemPrice + MILESTONE_AMOUNT_ROUNDING_TOLERANCE) {
    return true
  }
  if (payable && roundedSum < payable - MILESTONE_AMOUNT_ROUNDING_TOLERANCE) {
    return true
  }
  return false
}

export const useProjectInvoicingMethod = (orgId: number | undefined | null, projectId: number | undefined | null) => {
  const [invoiceMode, setInvoiceMode] = useState<InvoiceModeValue>(null)
  const [invoiceModeDisplay, setInvoiceModeDisplay] = useState<InvoiceModeDisplay>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasAnyPaymentRequested, setHasAnyPaymentRequested] = useState(false)
  const [orgInvoiceMode, setOrgInvoiceMode] = useState<InvoiceModeValue>(null)
  const [lastCounter, setLastCounter] = useState(0)

  const fetchInvoiceMode = useCallback(() => {
    setIsLoading(true)
    return restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/projects/${projectId}/invoice_mode/`,
    })
      .then((res) => {
        setInvoiceMode(res.data.invoice_mode)
        setInvoiceModeDisplay(getInvoiceModeDisplay(res.data.invoice_mode))
        setHasAnyPaymentRequested(res.data.has_any_payment_requested)
        setOrgInvoiceMode(res.data.org_invoice_mode)
      })
      .catch((err) => {
        setInvoiceMode(null)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [orgId, projectId])

  useEffect(() => {
    if (orgId && projectId) {
      fetchInvoiceMode()
    }
  }, [orgId, projectId, fetchInvoiceMode])

  useEffect(() => {
    const interval = setInterval(() => {
      if (invoiceModeRefreshCounter !== lastCounter) {
        setLastCounter(invoiceModeRefreshCounter)
        if (orgId && projectId) {
          fetchInvoiceMode()
        }
      }
    }, 200)
    return () => clearInterval(interval)
  }, [lastCounter, orgId, projectId, fetchInvoiceMode])

  return {
    invoiceMode,
    invoiceModeDisplay,
    hasAnyPaymentRequested,
    orgInvoiceMode,
    refresh: fetchInvoiceMode,
    isLoading,
  }
}

export const updateProjectInvoiceMethod = (
  orgId: number | undefined | null,
  projectId: number | undefined | null,
  forceEnableInvoicing: boolean
) => {
  return new Promise((resolve, reject) => {
    restClientInstance('CUSTOM_PUT', 'custom', {
      url: `orgs/${orgId}/projects/${projectId}/invoice_mode/`,
      data: {
        force_enable_invoicing: forceEnableInvoicing,
      },
    })
      .then((res) => {
        triggerInvoiceModeRefresh()
        resolve(res)
      })
      .catch((err) => {
        reject(err?.body?.message)
      })
  })
}

/** Statuses for which a new unsent milestone payment may exist alongside the old request (see useGetPaymentRequests). */
const SUPERSEDED_MILESTONE_PAYMENT_STATUSES: PaymentRequestStatusType[] = [
  'cancelled',
  'refunded',
  'chargeback complete',
]

function paymentRequestsMatchingMilestone(
  milestone: ExpectedMilestonePaymentType,
  paymentRequests: PaymentRequestType[]
): PaymentRequestType[] {
  return paymentRequests.filter((pmtReq) => {
    const configMilestoneMatch =
      !pmtReq.payment_milestone_override_uuid &&
      milestone.payment_milestone_configuration_id &&
      pmtReq.payment_milestone_configuration &&
      pmtReq.payment_milestone_configuration === milestone.payment_milestone_configuration_id &&
      pmtReq.payment_number === milestone.payment_number

    const overrideMilestoneMatch =
      pmtReq.payment_milestone_override_uuid && pmtReq.payment_milestone_override_uuid === milestone.override_uuid

    return configMilestoneMatch || overrideMilestoneMatch
  })
}

/**
 * After a refund (or similar), the combined payment-request list can include both the superseded
 * request and a synthetic `unsent` row for the same milestone. The timeline must show one cell per
 * milestone — prefer the active/upcoming request over superseded ones.
 */
function pickPrimaryPaymentRequestForMilestoneMerge(matching: PaymentRequestType[]): PaymentRequestType | null {
  if (matching.length === 0) return null
  if (matching.length === 1) return matching[0]

  const nonSuperseded = matching.filter((p) => !SUPERSEDED_MILESTONE_PAYMENT_STATUSES.includes(p.status))
  const pool = nonSuperseded.length > 0 ? nonSuperseded : matching

  return pool.reduce((best, cur) => {
    if (best.status === 'unsent' && cur.status !== 'unsent') return best
    if (cur.status === 'unsent' && best.status !== 'unsent') return cur
    return (cur.id ?? 0) > (best.id ?? 0) ? cur : best
  })
}

export const mergeCancelledPaymentsIntoMilestones = (
  milestones: ExpectedMilestonePaymentType[],
  paymentRequests: PaymentRequestType[]
) => {
  const all: ExpectedMilestonePaymentType[] = []
  milestones.forEach((milestone) => {
    const matching = paymentRequestsMatchingMilestone(milestone, paymentRequests)
    const pmtReq = pickPrimaryPaymentRequestForMilestoneMerge(matching)
    if (pmtReq) {
      all.push({ ...milestone, status: pmtReq.status, archived: pmtReq.archived })
    }
  })

  return all
}
